import { couponKindOf, couponOfferOf, type CouponKind, type CouponOffer } from "@/lib/coupon-benefit"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import type { ExposedCoupon } from "@/types/external/exposed-coupon"

/** How the home carousel prints a coupon's benefit: the small `title` over the big `value`, and the `phrase` screen readers get. */
export type ExposedCouponBenefit = { title: string; value: string; phrase: string }

function percentText(percentage: number): string {
  return `${percentage.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
}

function legacyBenefit(coupon: ExposedCoupon): ExposedCouponBenefit {
  const percentage = Number(coupon.discountPercentage) || 0
  const amount = Number(coupon.discountValue) || 0
  if (percentage > 0) return { title: "Desconto", value: percentText(percentage), phrase: `${percentText(percentage)} de desconto` }
  if (amount > 0) return { title: "Desconto", value: formatBRL(amount), phrase: `${formatBRL(amount)} de desconto` }
  return { title: "Cupom", value: "Desconto", phrase: "Desconto" }
}

function freeItemsBenefit(offer: Extract<CouponOffer, { kind: "free-items" }>): ExposedCouponBenefit {
  const names = offer.refs.map((ref) => ref.name.trim()).filter(Boolean)
  const { quantity } = offer
  if (offer.refs.length === 1) {
    const value = names[0] ? (quantity === 1 ? `1 ${names[0]}` : `${quantity}x ${names[0]}`) : quantity === 1 ? "1 item" : `${quantity} itens`
    return { title: "Item grátis", value, phrase: `${value} grátis` }
  }
  const count = offer.pick === "one" ? quantity : offer.refs.length * quantity
  const value = count === 1 ? "1 item" : `${count} itens`
  const choice = offer.pick === "one" ? "à sua escolha" : "todos eles"
  return { title: "Item grátis", value, phrase: `${value} grátis, ${choice}` }
}

function offerBenefit(offer: CouponOffer): ExposedCouponBenefit {
  if (offer.kind === "free-shipping") return { title: "Frete", value: "Grátis", phrase: "Frete grátis" }
  if (offer.kind === "free-items") return freeItemsBenefit(offer)
  const { reward } = offer
  const value = reward.kind === "percent" ? percentText(reward.basisPoints / 100) : reward.kind === "amount" ? formatBRL(centsToReais(reward.cents)) : "Desconto"
  return { title: "Desconto", value, phrase: `${value} de desconto` }
}

function kindBenefit(kind: CouponKind): ExposedCouponBenefit | null {
  if (kind === "free-shipping") return { title: "Frete", value: "Grátis", phrase: "Frete grátis" }
  if (kind === "free-items") return { title: "Brinde", value: "Grátis", phrase: "Item grátis" }
  return null
}

/** What an exposed coupon gives for the carousel: its offer first, else its kind, else the legacy discount fields; a free-shipping or free-items coupon never reads as "0%". */
export function exposedCouponBenefit(coupon: ExposedCoupon): ExposedCouponBenefit {
  const offer = couponOfferOf(coupon.offer)
  if (offer) return offerBenefit(offer)
  const kind = couponKindOf(coupon.kind)
  return (kind && kindBenefit(kind)) ?? legacyBenefit(coupon)
}
